import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: number | string;
  label: string;
}

interface SearchableSelectProps {
  label?: string;
  options: Option[];
  value: number | string | null;
  onChange: (value: number | string | null) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export default function SearchableSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Search and select...',
  required = false,
  disabled = false,
  error,
  className = '',
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected option label
  const selectedOption = options.find(option => option.value === value);

  // Handle option selection
  const handleOptionSelect = (optionValue: number | string) => {
    onChange(optionValue);
    setSearchTerm('');
    // Small delay to show selection before closing
    setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const errorClass = error ? 'border-red-500' : 'border-gray-300';

  return (
    <div className={`w-full relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={isOpen ? searchTerm : (selectedOption?.label || '')}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border rounded-lg min-h-[44px] lg:min-h-0
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
            transition-colors duration-200
            disabled:bg-gray-100 disabled:cursor-not-allowed
            ${errorClass}
          `}
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <i className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-400`}></i>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">No options found</div>
          ) : (
            filteredOptions.map(option => (
              <div
                key={option.value}
                onClick={() => handleOptionSelect(option.value)}
                className={`
                  px-3 py-2.5 lg:py-2 cursor-pointer hover:bg-gray-50 active:bg-brand-50 flex items-center justify-between text-sm
                  ${value === option.value ? 'bg-brand-50 text-brand-700' : 'text-gray-900'}
                `}
              >
                <span className="truncate">{option.label}</span>
                {value === option.value && (
                  <i className="fas fa-check text-brand-600 ml-2 flex-shrink-0"></i>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}